import { CSSProperties, useContext, useEffect, useState } from "react";

import { DEFAULT_BRANDING } from "../../../config/branding";
import { StateContext } from "../../../context/globalContext/context";
import { iGlobalContext } from "../../../context/globalContext/interface";
import { isPartnerBranding } from "../../../utils/partnerBranding";

const rgbToHsl = (r: number, g: number, b: number): [number, number, number] => {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
  else if (max === gn) h = ((bn - rn) / d + 2) / 6;
  else h = ((rn - gn) / d + 4) / 6;
  return [h * 360, s, l];
};

const hslToRgb = (h: number, s: number, l: number): [number, number, number] => {
  const hn = (((h % 360) + 360) % 360) / 360;
  if (s === 0) {
    const v = Math.round(l * 255);
    return [v, v, v];
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const channel = (t: number): number => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  return [
    Math.round(channel(hn + 1 / 3) * 255),
    Math.round(channel(hn) * 255),
    Math.round(channel(hn - 1 / 3) * 255),
  ];
};

const parseHexColor = (hex: string): [number, number, number] | null => {
  const raw = String(hex || "").trim().replace("#", "");
  const full = raw.length === 3 ? raw.split("").map((c) => c + c).join("") : raw;
  if (!/^[0-9a-f]{6}$/i.test(full)) return null;
  return [parseInt(full.slice(0, 2), 16), parseInt(full.slice(2, 4), 16), parseInt(full.slice(4, 6), 16)];
};

const HUE_TOLERANCE = 40;
const MIN_SATURATION = 0.12;

const recolor = (src: string, targetHex: string): Promise<string | null> =>
  new Promise((resolve) => {
    const source = parseHexColor(DEFAULT_BRANDING.primaryColor);
    const target = parseHexColor(targetHex);
    if (!source || !target) {
      resolve(null);
      return;
    }
    const [srcH, srcS, srcL] = rgbToHsl(source[0], source[1], source[2]);
    const [tgtH, tgtS, tgtL] = rgbToHsl(target[0], target[1], target[2]);
    const satRatio = srcS > 0 ? tgtS / srcS : 1;
    const lightRatio = srcL > 0 ? tgtL / srcL : 1;

    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        const scale = /\.svg(\?|$)/i.test(src) ? 3 : 1;
        canvas.width = img.naturalWidth * scale;
        canvas.height = img.naturalHeight * scale;
        const ctx = canvas.getContext("2d");
        if (!ctx || !canvas.width || !canvas.height) {
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const px = data.data;
        for (let i = 0; i < px.length; i += 4) {
          if (px[i + 3] === 0) continue;
          const [h, s, l] = rgbToHsl(px[i], px[i + 1], px[i + 2]);
          if (s < MIN_SATURATION) continue;
          let delta = Math.abs(h - srcH);
          if (delta > 180) delta = 360 - delta;
          if (delta > HUE_TOLERANCE) continue;
          const [r, g, b] = hslToRgb(
            tgtH + (h - srcH),
            Math.min(1, Math.max(0, s * satRatio)),
            Math.min(1, Math.max(0, l * lightRatio))
          );
          px[i] = r;
          px[i + 1] = g;
          px[i + 2] = b;
        }
        ctx.putImageData(data, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });

const recolorCache = new Map<string, Promise<string | null>>();

const recolorCached = (src: string, targetHex: string): Promise<string | null> => {
  const key = `${src}|${targetHex}`;
  const hit = recolorCache.get(key);
  if (hit) return hit;
  const pending = recolor(src, targetHex);
  recolorCache.set(key, pending);
  return pending;
};

export type BrandedRasterIconProps = {
  src: string;
  alt?: string;
  className?: string;
  brandColor?: string;
  usePartnerIcon?: boolean;
  width?: number | string;
  height?: number | string;
  style?: CSSProperties;
  onClick?: (event: React.MouseEvent<HTMLImageElement>) => void;
  "aria-hidden"?: boolean | "true" | "false";
};

function BrandedRasterIcon({
  src,
  alt = "",
  className = "",
  brandColor: brandColorProp,
  usePartnerIcon = false,
  width,
  height,
  style,
  onClick,
  "aria-hidden": ariaHidden,
}: BrandedRasterIconProps): JSX.Element {
  const { branding }: iGlobalContext = useContext(StateContext);
  const partner = isPartnerBranding(branding);
  const brandColor = brandColorProp || branding?.primaryColor || DEFAULT_BRANDING.primaryColor;
  const partnerIcon = partner && usePartnerIcon ? branding?.iconUrl || "" : "";
  const [recolored, setRecolored] = useState<string | null>(null);
  const isDefaultBrand =
    !partner || !brandColor || brandColor.toUpperCase() === DEFAULT_BRANDING.primaryColor.toUpperCase();

  useEffect(() => {
    if (isDefaultBrand || partnerIcon) {
      setRecolored(null);
      return undefined;
    }
    let live = true;
    recolorCached(src, brandColor).then((url) => {
      if (live) setRecolored(url);
    });
    return () => {
      live = false;
    };
  }, [src, brandColor, isDefaultBrand, partnerIcon]);

  const pending = !isDefaultBrand && !partnerIcon && !recolored;

  return (
    <img
      src={partnerIcon || recolored || src}
      alt={alt}
      className={className}
      width={partner ? width : undefined}
      height={partner ? height : undefined}
      style={pending ? { ...style, visibility: "hidden" } : style}
      onClick={onClick}
      aria-hidden={ariaHidden}
    />
  );
}

export default BrandedRasterIcon;
